import React, { useEffect, useRef, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import HCaptchaField from "@/components/HCaptchaField";

const PROPERTY_TYPES = [
  { v: "any", l: "Any" },
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "plot", l: "Plot / Land" },
  { v: "agricultural", l: "Agricultural" },
];

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  budget_min: "",
  budget_max: "",
  city: "Rewa",
  locality: "",
  locality_other: "",
  property_type: "any",
  listing_type: "any",
  bhk: "",
  timeline: "",
  notes: "",
};

const INPUT_CLS = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";
const LABEL_CLS = "text-[10px] uppercase tracking-widest text-muted-foreground mb-1";

/** Form state, geo lookups and submission for the requirement modal. */
function useRequirementForm(open, user, onClose) {
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [localityMode, setLocalityMode] = useState("select"); // "select" | "other"
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const [f, setF] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    api.get("/geo/cities").then((r) => setCities(r.data.items || []));
  }, [open]);

  useEffect(() => {
    if (!f.city) { setLocalities([]); return; }
    api.get(`/geo/localities?city=${encodeURIComponent(f.city)}`).then((r) => setLocalities(r.data.items || []));
  }, [f.city]);

  const validate = () => {
    if (!f.city.trim()) { toast.error("City is required"); return false; }
    if (!user && (!f.phone || f.phone.replace(/\D/g, "").length < 10)) {
      toast.error("Please enter a valid 10-digit mobile so Tharlo can reach you");
      return false;
    }
    if (!user && !captchaToken) {
      toast.error("Please complete the captcha to continue");
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const locality = localityMode === "other" ? f.locality_other : f.locality;
    return {
      name: f.name || undefined,
      phone: f.phone || undefined,
      email: f.email || undefined,
      budget_min: f.budget_min ? parseFloat(f.budget_min) : null,
      budget_max: f.budget_max ? parseFloat(f.budget_max) : null,
      city: f.city,
      locality: locality || "",
      property_type: f.property_type,
      listing_type: f.listing_type,
      bhk: f.bhk,
      timeline: f.timeline,
      notes: f.notes,
      hcaptcha_token: user ? undefined : captchaToken,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/requirements", buildPayload());
      toast.success("Requirement posted. Tharlo will match you with the right properties.");
      onClose();
      setF({ ...f, notes: "", budget_min: "", budget_max: "", locality: "", locality_other: "" });
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } catch (err) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
      const detail = err?.response?.data?.detail;
      if (detail === "captcha_failed") {
        toast.error("Please tick the 'I am human' checkbox above and try again.");
      } else {
        toast.error(detail || "Failed to submit. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return { f, setF, cities, localities, busy, localityMode, setLocalityMode, captchaRef, setCaptchaToken, submit };
}

function GuestContactFields({ f, setF }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <input data-testid="req-name" className={INPUT_CLS} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="req-phone" className={INPUT_CLS} placeholder="Mobile (10 digits)*" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required />
    </div>
  );
}

function CityField({ form }) {
  const { f, setF, cities, setLocalityMode } = form;
  return (
    <div>
      <div className={LABEL_CLS}>City *</div>
      {cities.length > 0 ? (
        <select data-testid="req-city" className={INPUT_CLS} value={f.city} onChange={(e) => { setF({ ...f, city: e.target.value, locality: "", locality_other: "" }); setLocalityMode("select"); }}>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          <option value="__other__">Other city...</option>
        </select>
      ) : (
        <input className={INPUT_CLS} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} required />
      )}
      {f.city === "__other__" && (
        <input data-testid="req-city-other" className={INPUT_CLS + " mt-2"} placeholder="Enter city name" onChange={(e) => setF({ ...f, city: e.target.value })} required />
      )}
    </div>
  );
}

function LocalityField({ form }) {
  const { f, setF, localities, localityMode, setLocalityMode } = form;
  return (
    <div>
      <div className={LABEL_CLS}>Locality</div>
      {localityMode === "select" ? (
        <select data-testid="req-locality" className={INPUT_CLS} value={f.locality} onChange={(e) => {
          if (e.target.value === "__other__") { setLocalityMode("other"); setF({ ...f, locality: "" }); }
          else setF({ ...f, locality: e.target.value });
        }}>
          <option value="">Any locality</option>
          {localities.map((l) => <option key={l} value={l}>{l}</option>)}
          <option value="__other__">Other (specify)...</option>
        </select>
      ) : (
        <div className="flex gap-2">
          <input data-testid="req-locality-other" className={INPUT_CLS} placeholder="Type locality name" value={f.locality_other} onChange={(e) => setF({ ...f, locality_other: e.target.value })} />
          <button type="button" onClick={() => { setLocalityMode("select"); setF({ ...f, locality_other: "" }); }} className="text-xs text-gold hover:underline whitespace-nowrap">← List</button>
        </div>
      )}
    </div>
  );
}

function BudgetFields({ f, setF }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className={LABEL_CLS}>Budget Min (₹)</div>
        <input data-testid="req-budget-min" type="number" className={INPUT_CLS} placeholder="e.g. 2000000" value={f.budget_min} onChange={(e) => setF({ ...f, budget_min: e.target.value })} />
      </div>
      <div>
        <div className={LABEL_CLS}>Budget Max (₹)</div>
        <input data-testid="req-budget-max" type="number" className={INPUT_CLS} placeholder="e.g. 8000000" value={f.budget_max} onChange={(e) => setF({ ...f, budget_max: e.target.value })} />
      </div>
    </div>
  );
}

function TypePurposeFields({ f, setF }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className={LABEL_CLS}>Property Type</div>
        <select data-testid="req-ptype" className={INPUT_CLS} value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
          {PROPERTY_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </div>
      <div>
        <div className={LABEL_CLS}>Purpose</div>
        <select data-testid="req-listing" className={INPUT_CLS} value={f.listing_type} onChange={(e) => setF({ ...f, listing_type: e.target.value })}>
          <option value="any">Any</option>
          <option value="sale">Buy</option>
          <option value="rent">Rent</option>
        </select>
      </div>
    </div>
  );
}

function BhkTimelineFields({ f, setF }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className={LABEL_CLS}>BHK (if residential)</div>
        <input data-testid="req-bhk" className={INPUT_CLS} placeholder="e.g. 2 or 3+" value={f.bhk} onChange={(e) => setF({ ...f, bhk: e.target.value })} />
      </div>
      <div>
        <div className={LABEL_CLS}>Timeline</div>
        <select data-testid="req-timeline" className={INPUT_CLS} value={f.timeline} onChange={(e) => setF({ ...f, timeline: e.target.value })}>
          <option value="">Select</option>
          <option value="immediate">Immediate (within 1 month)</option>
          <option value="3_months">Within 3 months</option>
          <option value="6_months">Within 6 months</option>
          <option value="exploring">Just exploring</option>
        </select>
      </div>
    </div>
  );
}

export default function RequirementModal({ open, onClose }) {
  const { user } = useAuth();
  const form = useRequirementForm(open, user, onClose);

  if (!open) return null;

  const { f, setF, busy, captchaRef, setCaptchaToken, submit } = form;

  return (
    <div
      className="fixed inset-0 z-[110] bg-ink/70 backdrop-blur-sm flex items-start justify-center p-4 pt-6 md:pt-10 overflow-y-auto"
      data-testid="requirement-modal"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-xl mb-8 border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white">
          <div>
            <div className="overline flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-gold" /> Buyer Concierge</div>
            <h2 className="font-display text-xl text-ink mt-0.5">Post Your Requirement</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Shared privately with Tharlo advisors — not with any broker.</p>
          </div>
          <button onClick={onClose} data-testid="req-close" className="p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3" data-testid="requirement-form">
          {!user && <GuestContactFields f={f} setF={setF} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CityField form={form} />
            <LocalityField form={form} />
          </div>

          <BudgetFields f={f} setF={setF} />
          <TypePurposeFields f={f} setF={setF} />
          <BhkTimelineFields f={f} setF={setF} />

          <div>
            <div className={LABEL_CLS}>Tell us more about your need</div>
            <textarea
              data-testid="req-notes"
              className={INPUT_CLS}
              rows={4}
              placeholder="Anything specific you'd like Tharlo to know — vastu preference, floor, gated society, near school, ready-to-move, etc."
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
            />
          </div>

          <HCaptchaField
            ref={captchaRef}
            skip={!!user}
            onVerify={(t) => setCaptchaToken(t)}
            onExpire={() => setCaptchaToken(null)}
          />

          <button data-testid="req-submit" type="submit" disabled={busy} className="btn-gold w-full">
            {busy ? "Submitting..." : "Send to Tharlo Advisor"}
          </button>
          <p className="text-[11px] text-muted-foreground text-center">Your details stay with Tharlo. We'll shortlist properties and reach out.</p>
        </form>
      </div>
    </div>
  );
}
